import { useEffect, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { env } from "./config";
import { extractOrgId, hasViewerRole } from "./roles";
import { LoginScreen } from "../components/LoginScreen";
import { Forbidden } from "../components/Forbidden";

type GateReason = "wrong-tenant" | "missing-role";

export function TenantGate({ children }: { children: ReactNode }) {
  const auth = useAuth();

  useEffect(() => {
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.error
    ) {
      void auth.signinRedirect();
    }
  }, [auth]);

  if (auth.isLoading || auth.activeNavigator) {
    return <LoginScreen label="Signing you in…" />;
  }

  if (auth.error) {
    return (
      <Forbidden
        reason="auth-error"
        detail={auth.error.message}
        onRetry={() => void auth.signinRedirect()}
      />
    );
  }

  if (!auth.isAuthenticated || !auth.user) {
    return <LoginScreen label="Redirecting to sign in…" />;
  }

  const profile = auth.user.profile;
  const orgId = extractOrgId(profile, env.projectId);
  const hasRole = hasViewerRole(profile, env.projectId);

  let reason: GateReason | null = null;
  if (orgId !== env.orgId) reason = "wrong-tenant";
  else if (!hasRole) reason = "missing-role";

  if (reason) {
    return (
      <Forbidden
        reason={reason}
        onSignOut={() =>
          void auth.signoutRedirect({
            post_logout_redirect_uri: env.postLogoutRedirectUri
          })
        }
      />
    );
  }

  return <>{children}</>;
}
